import React, { useState, useEffect, useRef, useMemo, Suspense, lazy } from "react";
import { Routes, Route, Navigate, Outlet, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuthStore } from "./stores/authStore";
import {
  Box,
  useTheme,
  useMediaQuery,
  IconButton,
  Container,
  CircularProgress,
} from "@mui/material";
import { useSelectedFundFacetStore } from "./stores/fundFacetStore";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  useIdStore,
  useViewedTypeStore,
} from "./stores/selectedToViewStore";
import { UserRole } from "./interfaces/DTO/user";
import { buildRelativePublicTokenPath } from "./utils/featuresFormatter";
import { PORTAL_BASE, PORTAL_PUBLIC_BASE } from "./portal/portalRoutes";

// ✅ EAGER loading (נטענות מיד) - Consulting & Login
import { ConsultingSidebarContent } from "./components/Consulting/ConsultingSidebarContent";
import { PresentationSidebar } from "./components/Consulting/PresentationSidebar";
import { UserConsultingPage } from "./components/Consulting/UserConsultingPage";
import { NewConsultingPage } from "./pages/NewConsultingPage";
import LoginPage from "./pages/LoginPage";

// ✅ LAZY loading - כל שאר הקומפוננטות
const HomePage = lazy(() => import("./pages/HomePage").then(m => ({ default: m.HomePage })));
const AboutPage = lazy(() => import("./pages/AboutPage").then(m => ({ default: m.AboutPage })));
const UsersSidebarContent = lazy(() => import("./components/Users/UsersSidebarContent").then(m => ({ default: m.UsersSidebarContent })));
const GemelSidebarContent = lazy(() => import("./components/GemelData/GemelSidebarContent").then(m => ({ default: m.GemelSidebarContent })));
const LoadedProductionsPage = lazy(() => import("./pages/LoadedProductionsPage").then(m => ({ default: m.LoadedProductionsPage })));
const LoadedPreConsultingPage = lazy(() => import("./pages/LoadedPreConsultingPage").then(m => ({ default: m.LoadedPreConsultingPage })));
const MislakaApiPage = lazy(() => import("./pages/MislakaApiPage").then(m => ({ default: m.MislakaApiPage })));
const PolicyChangesPage = lazy(() => import("./pages/PolicyChangesPage").then(m => ({ default: m.default })));
const AdminCompaniesPage = lazy(() => import("./components/Administration/AdminCompaniesPage").then(m => ({ default: m.default })));
const ProfileSidebarContent = lazy(() => import("./components/Profile/ProfileSidebarContent").then(m => ({ default: m.ProfileSidebarContent })));
const IntegrationsPage = lazy(() => import("./components/Profile/IntegrationsPage").then(m => ({ default: m.default })));
const ProducerCodesPage = lazy(() => import("./components/Profile/ProducerCodesPage").then(m => ({ default: m.default })));
const OAuthCallbackPage = lazy(() => import("./pages/OAuthCallbackPage").then(m => ({ default: m.default })));
const GoogleInboxPage = lazy(() => import("./pages/GoogleInboxPage").then(m => ({ default: m.default })));
const CompanyProfilePage = lazy(() => import("./components/Profile/CompanyProfilePage").then(m => ({ default: m.default })));
const MonthlyMislakaPage = lazy(() => import("./pages/MonthlyMislakaPage").then(m => ({ default: m.default })));
const PolicyRuleBasedInsightsPage = lazy(() => import("./components/Insights/PolicyRuleBasedInsightsPage").then(m => ({ default: m.default })));
const PolicyStatisticsPage = lazy(() => import("./components/Insights/PolicyStatisticsPage").then(m => ({ default: m.default })));
const BoardPage = lazy(() => import("./components/UserTask/BoardPage").then(m => ({ default: m.BoardPage })));
const UserTaskUploadPage = lazy(() => import("./components/UserTask/UserTaskUploadPage").then(m => ({ default: m.default })));
const CommissionConfigPage = lazy(() => import("./components/Profile/CommissionConfigPage").then(m => ({ default: m.default })));
const StrategyPage = lazy(() => import("./components/Profile/StrategyPage").then(m => ({ default: m.default })));
const PublicFormPage = lazy(() => import("./pages/public/PublicFormPage").then(m => ({ default: m.default })));
const LeadPortalPage = lazy(() => import("./pages/public/LeadPortalPage").then(m => ({ default: m.default })));
const ContactPortalPage = lazy(() => import("./pages/public/ContactPortalPage").then(m => ({ default: m.default })));
const ContactPortalApp = lazy(() => import("./portal/ContactPortalApp"));
const ContactPortalPublicApp = lazy(() => import("./portal/ContactPortalPublicApp"));
const DealPortalPage = lazy(() => import("./pages/public/DealPortalPage").then(m => ({ default: m.default })));
const ReportPortalPage = lazy(() => import("./pages/public/ReportPortalPage").then(m => ({ default: m.default })));
const SimulationPortalPage = lazy(() => import("./pages/public/SimulationPortalPage").then(m => ({ default: m.default })));
const PublicLinkTokenTable = lazy(() => import("./components/Users/PublicLinkTokenTable").then(m => ({ default: m.default })));
const ContactPortalStatusTable = lazy(() => import("./components/Users/ContactPortalStatusTable").then(m => ({ default: m.default })));
const PdfTemplateEditorPage = lazy(() => import("./pages/admin/PdfTemplateEditorPage").then(m => ({ default: m.default })));
const FormTemplateEditorPage = lazy(() => import("./pages/admin/FormTemplateEditorPage"));
const AgentProfilePage = lazy(() => import("./components/Profile/AgentProfilePage").then(m => ({ default: m.AgentProfilePage })));
const AgentPhoneBookOverrideTable = lazy(() => import("./components/Agent/AgentPhoneBookOverrideTable").then(m => ({ default: m.default })));
const HandlerBankDetailsTable = lazy(() => import("./components/Agent/HandlerBankDetailsTable").then(m => ({ default: m.default })));
const TasksTablePage = lazy(() => import("./components/UserTask/TasksTablePage").then(m => ({ default: m.default })));
const MimshakTransfersTable = lazy(() => import("./components/Agent/MimshakTransfersTable").then(m => ({ default: m.default })));
const YearEndDepositsTable = lazy(() => import("./components/Users/YearEndDepositsTable").then(m => ({ default: m.default })));
const MimshakMonthlyCommissionPage = lazy(() => import("./components/Commission/MimshakMonthlyCommissionPage").then(m => ({ default: m.default })));
const MimshakMonthlyBonusCommissionPage = lazy(() => import("./components/Commission/MimshakMonthlyBonusCommissionPage").then(m => ({ default: m.default })));
const RevIncomeSummaryPage = lazy(() => import("./components/Profile/RevIncomeSummaryPage").then(m => ({ default: m.default })));
const GemelInvestigationHome = lazy(() => import("./components/GemelData/GemelInvestigationHome").then(m => ({ default: m.GemelInvestigationHome })));
const FamiliesPage = lazy(() => import("./components/CRM/FamiliesPage").then(m => ({ default: m.default })));
const ContactsPage = lazy(() => import("./components/CRM/contacts/ContactsPage").then(m => ({ default: m.default })));
const InsuranceDataPage = lazy(() => import("./components/Insurance/InsuranceDataPage").then(m => ({ default: m.default })));
const InvestmentDataPage = lazy(() => import("./components/Investment/InvestmentDataPage").then(m => ({ default: m.default })));
const LoginAuditPage = lazy(() => import("./pages/profile/LoginAuditPage").then(m => ({ default: m.default })));
const MeetingHistoryPage = lazy(() => import("./pages/MeetingHistoryPage").then(m => ({ default: m.default })));
const CampaignsPage = lazy(() => import("./pages/CampaignsPage").then(m => ({ default: m.default })));
const PaymentTrackingPage = lazy(() => import("./pages/PaymentTrackingPage").then(m => ({ default: m.default })));
const LeadsPage = lazy(() => import("./pages/LeadsPage").then(m => ({ default: m.default })));
const DealsPage = lazy(() => import("./pages/DealsPage").then(m => ({ default: m.default })));
const SharedDealsPage = lazy(() => import("./pages/SharedDealsPage").then(m => ({ default: m.default })));
const UserConsultingPrintComposePage = lazy(() => import("./pages/UserConsultingPrintComposePage").then(m => ({ default: m.default })));
const MeetingModePage = lazy(() => import("./components/Consulting/MeetingMode/MeetingModePage").then(m => ({ default: m.default })));
interface ProtectedRouteProps {
  requireAuth: boolean; // האם הנתיב דורש אימות
  redirectPath?: string; // נתיב ברירת מחדל להפניה
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  requireAuth,
  redirectPath = "/consulting", // ברירת מחדל להפניה
}) => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const setRedirectPath = useAuthStore((state) => state.setRedirectPath);
  const location = useLocation();

  if (requireAuth && !isAuthenticated) {
    // שמירת הנתיב שאליו המשתמש ניסה לגשת, כולל query params
    setRedirectPath(location.pathname + location.search);
    return <Navigate to="/login" replace />;
  }

  if (!requireAuth && isAuthenticated) {
    // מניעת גישה ל-login אם מחובר
    return <Navigate to={redirectPath} replace />;
  }

  return <Outlet />;
};
interface AdminProtectedRouteProps {
  redirectPath?: string;
}

const AdminProtectedRoute: React.FC<AdminProtectedRouteProps> = ({ redirectPath = "/home" }) => {
  const { isAuthenticated, user } = useAuthStore();

  if (!isAuthenticated || user?.role !== UserRole.SUPERADMIN) {
    return <Navigate to={redirectPath} replace />;
  }

  return <Outlet />;
};
const LayoutSimple: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <Box
      sx={{
        marginLeft: "40px", // ❗ להתאים לגודל ה-Drawer אם יש תפריט צד
        width: "calc(100% - 40px)", // ❗ גודל מסך מלא פחות רוחב ה-Drawer
        padding: "20px",
        boxSizing: "border-box",
      }}
    >
      <Container maxWidth={false} disableGutters>{children}</Container>
    </Box>
  );
};

interface LayoutWithSidebarProps {
  sidebarContent: React.ReactNode;
  selectedItem?: any; // אלמנט נבחר (יכול להיות כל סוג של מידע)
  onBack?: () => void; // פונקציה שמופעלת בלחיצה על כפתור Back
  sidebarWidth?: string; // רוחב ה-Sidebar (ברירת מחדל: 350px)
}

const LayoutWithSidebar: React.FC<LayoutWithSidebarProps> = ({
  sidebarContent,
  selectedItem,
  onBack,
  sidebarWidth = "350px",
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();

  // ✅ במובייל, מסתירים את ה-Sidebar אם יש selectedItem או אם אנחנו ב-/consulting/new
  const shouldHideSidebar = isMobile && (selectedItem || location.pathname === "/consulting/new");

  return (
    <Box
      sx={{
        display: "flex",
        flex: 1,
        width: "100%",
        height: "100%", // מבטיח שהאזור יהיה תחום ולא יגרום לגלילה חיצונית
        overflow: "hidden", // מונע גלילה מחוץ לאזור ה-Main
      }}
    >
      {!shouldHideSidebar ? (
        <Box
          sx={{
            width: isMobile ? "100%" : sidebarWidth,
            marginLeft: isMobile ? 0 : "50px",
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflowY: "auto", // ✅ חשוב!
            overflowX: "hidden",
            transition: "width 0.3s ease-in-out",
          }}
        >
          {sidebarContent}
        </Box>
      ) : null}
 {/* ✅ אזור התוכן */}
      <Box
        sx={{
          flex: 1,
          p: 2,
          width: "100%",
          maxWidth: "100%",
          overflowY: "auto", // ✅ גלילה אנכית מופעלת כאן
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
          height: "100%", // מבטיח שהגובה יהיה נכון
          position: "relative",
        }}
      >
        {shouldHideSidebar && onBack && (
          <IconButton
            onClick={onBack}
            sx={{
              position: "absolute", // ✅ כפתור ABSOLUTE → לא תופס מקום
              top: 18, // ✅ מצמיד לפינה העליונה
              left: 8, // ✅ במצב LTR
              zIndex: 10, // ✅ מונע כיסוי על ידי תוכן אחר
              backgroundColor: theme.palette.background.paper,
              boxShadow: theme.shadows[2],
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        )}
        <Outlet />
      </Box>
    </Box>
  );
};

const HomeLayout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { selectedItem, setSelectedItem } = useSelectedFundFacetStore();
  const [mobileSelectedItem, setMobileSelectedItem] = useState<null | boolean>(null);

  const handleBack = () => {
    setSelectedItem(null);
    setMobileSelectedItem(null);
  };
  const handleMenuSelect = () => isMobile && setMobileSelectedItem(true);

  return (
    <LayoutWithSidebar
      sidebarContent={<GemelSidebarContent onSelect={handleMenuSelect} />}
      selectedItem={selectedItem || mobileSelectedItem}
      onBack={handleBack}
    />
  );
};
const UsersLayout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [selectedItem, setSelectedItem] = useState<null | boolean>(null); // או כל ערך שהוא

  const handleBack = () => setSelectedItem(null);
  const handleMenuSelect = () => isMobile && setSelectedItem(true);

  return (
    <LayoutWithSidebar
      sidebarContent={<UsersSidebarContent onSelect={handleMenuSelect} />}
      selectedItem={selectedItem}
      onBack={handleBack}
    />
  );
};
const ProfileLayout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [selectedItem, setSelectedItem] = useState<null | boolean>(null);

  const handleBack = () => setSelectedItem(null);
  const handleMenuSelect = () => isMobile && setSelectedItem(true);

  return (
    <LayoutWithSidebar
      sidebarContent={<ProfileSidebarContent onSelect={handleMenuSelect} />}
      selectedItem={selectedItem}
      onBack={handleBack}
    />
  );
};
const ConsultingLayout = () => {
  const { id, setId } = useIdStore();
  const { viewedType, setViewedType } = useViewedTypeStore();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const previousPathRef = useRef(location.pathname);
  const isResettingRef = useRef(false);

  const [localSelectedItem, setLocalSelectedItem] = useState<{
    type: string;
    id: number;
  }>({
    type: viewedType,
    id,
  });

  // קריאת פרמטר presentation מה-URL
  const isPresentationMode = useMemo(() => searchParams.get("presentation") === "true", [searchParams]);

  // ✅ קריאה מה-URL - רק בטעינה ראשונה של הדף (refresh בדפדפן)
  useEffect(() => {
    const urlId = searchParams.get("id");
    const urlType = searchParams.get("type");

    // טעינת id ו-type אם קיימים ו-store ריק
    if (urlId && urlType && (urlType === "USER" || urlType === "FAMILY") && !id && !viewedType) {
      const parsedId = parseInt(urlId, 10);
      if (!isNaN(parsedId)) {
        console.log("   ✅ מעדכן store מ-URL: id =", parsedId, "type =", urlType);
        setId(parsedId);
        setViewedType(urlType);
      }
    }
  }, [searchParams, id, viewedType, setId, setViewedType]);

  // ✅ האזנה לכפתור BACK של הדפדפן
  useEffect(() => {
    const handlePopState = () => {
      // קריאה ישירה מה-URL במקום מ-searchParams כדי למנוע לולאות
      const params = new URLSearchParams(window.location.search);
      const urlId = params.get("id");
      const urlType = params.get("type");

      if (urlId && urlType) {
        setId(parseInt(urlId));
        setViewedType(urlType as "USER" | "FAMILY");
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [setId, setViewedType]);

  // ✅ לוגיקה משולבת - מטפל גם באיפוס וגם בניווט
  useEffect(() => {
    // אם זה בדיוק אחרי איפוס, מדלגים
    if (isResettingRef.current) {
      isResettingRef.current = false;
      previousPathRef.current = location.pathname;
      return;
    }

    // אם עוברים ל-/consulting/new ויש ID קיים, מאפסים אותו
    if (location.pathname === "/consulting/new" && previousPathRef.current !== "/consulting/new" && id) {
      isResettingRef.current = true;
      setId(null);
      setViewedType(null);
      previousPathRef.current = location.pathname;
      return;
    }

    // אם יש ID חדש ב-/consulting/new, עוברים ל-/consulting
    if (id && location.pathname === "/consulting/new" && previousPathRef.current === "/consulting/new") {
      navigate("/consulting", { replace: true });
    }

    previousPathRef.current = location.pathname;
  }, [id, location.pathname, navigate, setId, setViewedType]);



  // פונקציה שרק מחזירה את ה-Sidebar (למובייל)
  const handleMobileBack = () => {
    // אם אנחנו ב-/consulting/new, חוזרים ל-/consulting כדי להציג את ה-Sidebar
    if (location.pathname === "/consulting/new") {
      navigate("/consulting", { replace: true });
    } else {
      // אם יש ID, רק מאפסים אותו כדי להציג שוב את ה-Sidebar
      setId(null);
    }
  };

  // בחירת sidebar content ורוחב לפי מצב presentation
  const sidebarContent = isPresentationMode ? <PresentationSidebar /> : <ConsultingSidebarContent />;
  const sidebarWidth = isPresentationMode ? "200px" : "350px";

  return (
    <LayoutWithSidebar
      sidebarContent={sidebarContent}
      sidebarWidth={sidebarWidth}
      selectedItem={id} // קובע אם להציג Sidebar או Main
      onBack={handleMobileBack} // מחזיר את ה-Sidebar במקום לאפס את הכל
    />
  );
};

export const AppRoutes: React.FC = () => {
  const { selectedItem, setSelectedItem } = useSelectedFundFacetStore(); // מאזין לשינויים ב-Store
  return (
    <Suspense fallback={
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    }>
        <Routes>

        {/* Contact Portal — two separate apps, completely outside agent layout */}
        <Route path={`${PORTAL_PUBLIC_BASE}/*`} element={<ContactPortalPublicApp />} />
        <Route path={`${PORTAL_BASE}/*`} element={<ContactPortalApp />} />

        {/* נתיב ה-Login אינו מוגן */}
        <Route element={<ProtectedRoute requireAuth={false} redirectPath="/home" />} >
          <Route path={buildRelativePublicTokenPath(":signedToken")} element={<PublicFormPage />} />
          <Route path="/public/lead/:token" element={<LeadPortalPage />} />
          <Route path="/public/contact/:token" element={<ContactPortalPage />} />
          <Route path="/public/deal/:token/deal/:dealId" element={<DealPortalPage />} />
          <Route path="/public/deal/:token/report" element={<ReportPortalPage />} />
          <Route path="/public/simulation/:token" element={<SimulationPortalPage />} />
          <Route path="/login" element={<LoginPage />} />

        </Route>

        {/* נתיבים מוגנים */}
        <Route element={<ProtectedRoute requireAuth={true} />}>
          {/* דפים ללא Sidebar */}
          {/* Home - עם Sidebar ותמיכה ב-Back */}
          <Route path="/home" element={<HomeLayout />}>
            <Route path="" element={<GemelInvestigationHome />} />
          </Route>
          <Route path="/about" element={<AboutPage />} />
       
          {/* דפים עם Sidebar מותאם */}
          {/* Consulting - עם Sidebar ותמיכה ב-Back */}
          <Route path="/consulting/*" element={<ConsultingLayout />}>
            <Route path="" element={<UserConsultingPage />} />
            <Route path="new" element={<NewConsultingPage />} />
            {/* <Route path="existing" element={<ConsultingPage />} /> */}
          </Route>
          {/* Print route - ללא Sidebar/Layout (shell-less) */}
          <Route path="/consulting/:id/print" element={<UserConsultingPrintComposePage />} />
          {/* Meeting Mode route - ללא Sidebar/Layout (shell-less) */}
          <Route path="/consulting/:id/meeting-mode" element={<MeetingModePage />} />
          <Route path="/profile/*" element={<ProfileLayout />}>

            <Route path="company" element={<CompanyProfilePage />} />
            <Route path="agent" element={<AgentProfilePage />} />
            <Route path="producer-codes" element={<ProducerCodesPage />} />
            <Route path="monthly-commission" element={<MimshakMonthlyCommissionPage />} />
            <Route path="monthly-bonus-commission" element={<MimshakMonthlyBonusCommissionPage />} />
            <Route path="rev-income-summary" element={<RevIncomeSummaryPage />} />
            <Route path="phonebook" element={<AgentPhoneBookOverrideTable />} />
            <Route path="bank-details" element={<HandlerBankDetailsTable />} />
            <Route path="managment-fees" element={<CommissionConfigPage />} />
            <Route path="integrations" element={<IntegrationsPage />} />
            <Route path="audit" element={<LoginAuditPage />} />
            <Route path="strategy" element={<StrategyPage />} />
          </Route>
          {/* OAuth callback route - מחוץ לProfileLayout */}
          <Route path="/oauth/callback" element={<OAuthCallbackPage />} />
          {/* Google Inbox Page */}
          <Route path="/inbox" element={<GoogleInboxPage />} />
          <Route
            path="/users/*"
           element={<UsersLayout />}
          >
            <Route path="" element={<LoadedProductionsPage />} />
            <Route path="tasks-table" element={<TasksTablePage />} />
            <Route
              path="policy-changes"
              element={<PolicyChangesPage />}
            />
             <Route
              path="pre-consulting"
              element={<LoadedPreConsultingPage />}
            />
            <Route path="leads" element={<LeadsPage />} />
            <Route path="deals" element={<DealsPage />} />
            <Route path="shared-deals" element={<SharedDealsPage />} />
            <Route path="insurance-data" element={<InsuranceDataPage />} />
            <Route path="investment-data" element={<InvestmentDataPage />} />
            <Route path="contacts" element={<ContactsPage />} />
            <Route path="families" element={<FamiliesPage />} />
            <Route path="contact-portal-status" element={<ContactPortalStatusTable />} />
            <Route path="transfers" element={<MimshakTransfersTable />} />
            <Route path="year-end-deposits" element={<YearEndDepositsTable />} />
            <Route path="tasks" element={<BoardPage />} />
            <Route path="api" element={<MislakaApiPage />} />
            <Route path="insights" element={<PolicyRuleBasedInsightsPage />} />
            <Route path="statistics" element={<PolicyStatisticsPage />} />
            <Route path="montly-mislaka" element={<MonthlyMislakaPage />} />
            <Route path="daily-mislaka" element={<UserTaskUploadPage />} />
            <Route path="public-links" element={<PublicLinkTokenTable />} />
            <Route path="meeting-history" element={<MeetingHistoryPage />} />
            <Route path="campaigns" element={<CampaignsPage />} />
            <Route path="payment-tracking" element={<PaymentTrackingPage />} />
            {/* <Route path="fees" element={<FeeComparisonPage />} /> */}
          </Route>
          <Route element={<AdminProtectedRoute />}>
            <Route path="/admin/companies" element={
                <LayoutSimple>
                  <AdminCompaniesPage />
                </LayoutSimple>
              } />
            <Route path="/admin/forms" element={
                <LayoutSimple>
                  <PdfTemplateEditorPage />
                </LayoutSimple>
              } />
            <Route path="/admin/form-templates" element={
                <LayoutSimple>
                  <FormTemplateEditorPage />
                </LayoutSimple>
              } />
          </Route>
        </Route>

        {/* ברירת מחדל */}
        <Route path="/" element={<Navigate to="/consulting" replace />} />
        <Route path="*" element={<p>Page not found (404)</p>} />
      </Routes>
    </Suspense>
  );
};
export {};
